import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardBody, Button, Spinner, Alert, Switch } from '@components/ui';
import { PlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useInvoices } from '../hooks/useInvoices';
import { InvoiceStatus } from '../types/invoice.types';
import { ElectronicSignatureModal } from './ElectronicSignatureModal';
import { CertificateProcessModal } from './CertificateProcessModal';
import { companyService } from '@features/companies/services/companyService';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';
import { useInvoiceFilterStore, filterErrorInvoices } from '@/stores/invoiceFilterStore';
import { useSelectedCompany } from '@features/company/hooks/useSelectedCompany';

const statusColors = {
  [InvoiceStatus.DRAFT]: 'bg-secondary-100 text-secondary-800',
  [InvoiceStatus.PENDING]: 'bg-warning-100 text-warning-800',
  [InvoiceStatus.APPROVED]: 'bg-success-100 text-success-800',
  [InvoiceStatus.REJECTED]: 'bg-danger-100 text-danger-800',
};

const statusLabels = {
  [InvoiceStatus.DRAFT]: 'Borrador',
  [InvoiceStatus.PENDING]: 'Pendiente',
  [InvoiceStatus.APPROVED]: 'Aprobada',
  [InvoiceStatus.REJECTED]: 'Rechazada',
};

export function InvoiceListPage() {
  const [page, setPage] = useState(1);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showCertificateProcessModal, setShowCertificateProcessModal] = useState(false);
  const [isCheckingCertificates, setIsCheckingCertificates] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);
  const { data: selectedCompanyData } = useSelectedCompany();
  const companyId = (selectedCompanyData as any)?.company?.id || selectedCompanyData?.id;
  const { data, isLoading, error, refetch } = useInvoices({ page, pageSize: 10, companyId });

  // Global filter state (persists across views)
  const { hideErrorInvoices, setHideErrorInvoices } = useInvoiceFilterStore();

  // Filter invoices to hide those with errors if toggle is on (uses global store)
  const filteredInvoices = filterErrorInvoices(data?.invoices || [], hideErrorInvoices);

  const handleNewInvoiceClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsCheckingCertificates(true);

    const checkWithRetry = async (retries = 2): Promise<void> => {
      try {
        const result = await companyService.checkClientCertificates();

        if (result.hasCertificates) {
          // Has certificates, go directly to invoice creation
          navigate('/invoices/new');
        } else {
          // No certificates, show modal
          setShowSignatureModal(true);
        }
      } catch (error: any) {
        console.error('Error checking certificates:', error);

        // If error is 400 (No company selected), redirect to home
        if (error?.response?.status === 400) {
          console.log('No company selected, redirecting to home...');
          navigate(homeRoute);
          return;
        }

        // On transient errors, retry before giving up
        if (retries > 0) {
          console.warn(`Transient error checking certificates, retrying... (${retries} left)`);
          await new Promise(resolve => setTimeout(resolve, 1500));
          return checkWithRetry(retries - 1);
        }

        // After retries exhausted, let the user proceed to invoice creation
        console.warn('Could not verify certificates after retries, allowing user to proceed');
        navigate('/invoices/new');
      }
    };

    try {
      await checkWithRetry();
    } finally {
      setIsCheckingCertificates(false);
    }
  };

  const handleHaveCertificate = () => {
    setShowSignatureModal(false);
    // Redirect to company settings with fiscal tab selected
    navigate('/company/settings?tab=fiscal');
  };

  const handleNeedCertificate = () => {
    setShowSignatureModal(false);
    setShowCertificateProcessModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    const err = error as any;
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.message || 'Error desconocido';
    return (
      <Alert variant="danger" title="Error al cargar facturas">
        <p>
          {status ? `[${status}] ` : ''}
          {message}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
            Facturas
          </h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Gestiona tus facturas electrónicas
          </p>
        </div>
        <Button
          data-guide="Nueva Factura"
          className="w-full sm:w-auto"
          leftIcon={<PlusIcon className="w-5 h-5" />}
          onClick={handleNewInvoiceClick}
          isLoading={isCheckingCertificates}
        >
          Nueva Factura
        </Button>
      </div>

      {/* Filter controls */}
      <Card>
        <CardBody className="py-3 px-4">
          <div className="flex items-center justify-between">
            <Switch
              checked={hideErrorInvoices}
              onChange={setHideErrorInvoices}
              label="Ocultar facturas con error"
              size="sm"
            />
            {hideErrorInvoices && data?.invoices && (
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                {data.invoices.length - filteredInvoices.length} factura(s) con error oculta(s)
              </span>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                No hay facturas
              </h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                Comienza creando tu primera factura
              </p>
              <div className="mt-6">
                <Button
                  variant="primary"
                  onClick={handleNewInvoiceClick}
                  isLoading={isCheckingCertificates}
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Nueva Factura
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                <thead className="bg-secondary-50 dark:bg-secondary-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Número
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                  {filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900 dark:text-white">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-secondary-900 dark:text-white">
                          {invoice.customerName}
                        </div>
                        <div className="text-sm text-secondary-500 dark:text-secondary-400">
                          {invoice.customerRuc}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                        {invoice.date && format(new Date(invoice.date), 'dd/MM/yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-secondary-900 dark:text-white">
                        ${(invoice.totalAmount ?? 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            invoice.status ? (statusColors as any)[invoice.status] : ''
                          }`}
                        >
                          {invoice.status && (statusLabels as any)[invoice.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/pages/invoice/${invoice.id}`}
                          data-guide="Ver detalles"
                          className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400"
                        >
                          Ver detalles
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Paginación */}
      {data && filteredInvoices.length > 0 && (
        <Card>
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-secondary-700 dark:text-secondary-300">
                  Mostrando {(page - 1) * data.pageSize + 1} a{' '}
                  {Math.min(page * data.pageSize, data.total)} de {data.total} facturas
                </p>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">
                  Página {page} de {Math.ceil(data.total / data.pageSize)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * data.pageSize >= data.total}
                  onClick={() => setPage(page + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Electronic Signature Modal */}
      <ElectronicSignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onHaveCertificate={handleHaveCertificate}
        onNeedCertificate={handleNeedCertificate}
      />

      {/* Certificate Process Modal */}
      <CertificateProcessModal
        isOpen={showCertificateProcessModal}
        onClose={() => setShowCertificateProcessModal(false)}
        onComplete={() => {
          setShowCertificateProcessModal(false);
          navigate(homeRoute);
        }}
      />
    </div>
  );
}
